/**
 * Calendar-period algebra for sources whose period contract is Gregorian
 * calendar month. This module deliberately does not infer fiscal calendars.
 */

export const CALENDAR_PERIOD_TYPES = ['month', 'quarter', 'year'] as const;

export type CalendarPeriodType = (typeof CALENDAR_PERIOD_TYPES)[number];

export type CalendarPeriodWindow = Readonly<{
  type: CalendarPeriodType;
  from: string;
  to: string;
}>;

export type CalendarPeriodBounds = Readonly<{
  type: CalendarPeriodType;
  from: string;
  to: string;
  startMonth: string;
  endMonth: string;
  monthKeys: readonly string[];
}>;

export type CalendarGrowthComparison = 'previous_comparable_window' | 'year_over_year';

export type CalendarPeriodCoverage = Readonly<{
  status: 'complete' | 'partial';
  expectedMonthKeys: readonly string[];
  coveredMonthKeys: readonly string[];
  missingMonthKeys: readonly string[];
  /** A known source subtotal is always computable, even when a full period is not covered. */
  allowsObservedSum: true;
  /** Full-period and extrapolated claims require every calendar month in the window. */
  allowsFullPeriodClaim: boolean;
}>;

export type CalendarMonthRollup = Readonly<{
  monthKey: string;
  quarterKey: string;
  yearKey: string;
}>;

type CalendarPeriodWindowInput = Readonly<{
  type: string;
  from: string;
  to: string;
}>;

type ParsedCalendarPeriod = Readonly<{
  startMonthIndex: number;
  endMonthIndex: number;
}>;

const MONTH_KEY = /^(\d{4})-(0[1-9]|1[0-2])$/;
const QUARTER_KEY = /^(\d{4})-Q([1-4])$/;
const YEAR_KEY = /^\d{4}$/;

export function isCalendarPeriodWindow(
  value: CalendarPeriodWindowInput,
): value is CalendarPeriodWindow {
  return CALENDAR_PERIOD_TYPES.includes(value.type as CalendarPeriodType);
}

export function calendarPeriodBounds(
  period: CalendarPeriodWindow,
): CalendarPeriodBounds | null {
  const from = parseCalendarPeriod(period.type, period.from);
  const to = parseCalendarPeriod(period.type, period.to);
  if (!from || !to || to.endMonthIndex < from.startMonthIndex) return null;
  const monthKeys = monthKeysInRange(from.startMonthIndex, to.endMonthIndex);
  if (!monthKeys) return null;
  return {
    type: period.type,
    from: period.from,
    to: period.to,
    startMonth: monthKeys[0] as string,
    endMonth: monthKeys[monthKeys.length - 1] as string,
    monthKeys,
  };
}

export function calendarPriorPeriodWindow(
  period: CalendarPeriodWindow,
): CalendarPeriodWindow | null {
  const bounds = calendarPeriodBounds(period);
  if (!bounds) return null;
  const start = monthIndex(bounds.startMonth);
  const end = monthIndex(bounds.endMonth);
  if (start == null || end == null) return null;
  const length = end - start + 1;
  return calendarWindowForMonthBounds(period.type, start - length, end - length);
}

export function calendarGrowthWindow(input: Readonly<{
  period: CalendarPeriodWindow;
  comparison: CalendarGrowthComparison;
}>): CalendarPeriodWindow | null {
  if (input.comparison === 'previous_comparable_window') {
    return calendarPriorPeriodWindow(input.period);
  }
  const bounds = calendarPeriodBounds(input.period);
  if (!bounds) return null;
  const start = monthIndex(bounds.startMonth);
  const end = monthIndex(bounds.endMonth);
  if (start == null || end == null) return null;
  return calendarWindowForMonthBounds(input.period.type, start - 12, end - 12);
}

export function calendarRollupForMonth(monthKey: string): CalendarMonthRollup | null {
  const index = monthIndex(monthKey);
  if (index == null) return null;
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  return {
    monthKey,
    quarterKey: `${year.toString().padStart(4, '0')}-Q${Math.ceil(month / 3)}`,
    yearKey: year.toString().padStart(4, '0'),
  };
}

export function assessDeclaredCalendarPeriodCoverage(input: Readonly<{
  period: CalendarPeriodWindow;
  coveredMonthKeys: readonly string[];
}>): CalendarPeriodCoverage | null {
  const bounds = calendarPeriodBounds(input.period);
  if (!bounds) return null;
  const declared = new Set(input.coveredMonthKeys.filter((key) => monthIndex(key) != null));
  const coveredMonthKeys = bounds.monthKeys.filter((key) => declared.has(key));
  const missingMonthKeys = bounds.monthKeys.filter((key) => !declared.has(key));
  const complete = missingMonthKeys.length === 0;
  return {
    status: complete ? 'complete' : 'partial',
    expectedMonthKeys: bounds.monthKeys,
    coveredMonthKeys,
    missingMonthKeys,
    allowsObservedSum: true,
    allowsFullPeriodClaim: complete,
  };
}

function parseCalendarPeriod(
  type: CalendarPeriodType,
  key: string,
): ParsedCalendarPeriod | null {
  if (type === 'month') return parseMonthPeriod(key);
  if (type === 'quarter') return parseQuarterPeriod(key);
  return parseYearPeriod(key);
}

function parseMonthPeriod(key: string): ParsedCalendarPeriod | null {
  const index = monthIndex(key);
  return index == null ? null : { startMonthIndex: index, endMonthIndex: index };
}

function parseQuarterPeriod(key: string): ParsedCalendarPeriod | null {
  const match = QUARTER_KEY.exec(key);
  if (!match) return null;
  const year = Number.parseInt(match[1] as string, 10);
  const quarter = Number.parseInt(match[2] as string, 10);
  const startMonthIndex = year * 12 + (quarter - 1) * 3;
  return { startMonthIndex, endMonthIndex: startMonthIndex + 2 };
}

function parseYearPeriod(key: string): ParsedCalendarPeriod | null {
  if (!YEAR_KEY.test(key)) return null;
  const year = Number.parseInt(key, 10);
  const startMonthIndex = year * 12;
  return { startMonthIndex, endMonthIndex: startMonthIndex + 11 };
}

function calendarWindowForMonthBounds(
  type: CalendarPeriodType,
  startMonthIndex: number,
  endMonthIndex: number,
): CalendarPeriodWindow | null {
  const from = calendarKeyForMonthIndex(type, startMonthIndex, 'start');
  const to = calendarKeyForMonthIndex(type, endMonthIndex, 'end');
  return from && to ? { type, from, to } : null;
}

function calendarKeyForMonthIndex(
  type: CalendarPeriodType,
  index: number,
  boundary: 'start' | 'end',
): string | null {
  if (type === 'month') return monthKeyForIndex(index);
  const year = Math.floor(index / 12);
  const month = index % 12;
  if (year < 0 || year > 9999) return null;
  if (type === 'quarter') {
    if ((boundary === 'start' && month % 3 !== 0) || (boundary === 'end' && month % 3 !== 2)) {
      return null;
    }
    return `${year.toString().padStart(4, '0')}-Q${Math.floor(month / 3) + 1}`;
  }
  if ((boundary === 'start' && month !== 0) || (boundary === 'end' && month !== 11)) {
    return null;
  }
  return year.toString().padStart(4, '0');
}

function monthKeysInRange(start: number, end: number): readonly string[] | null {
  const keys: string[] = [];
  for (let index = start; index <= end; index += 1) {
    const key = monthKeyForIndex(index);
    if (!key) return null;
    keys.push(key);
  }
  return keys;
}

function monthIndex(key: string): number | null {
  const match = MONTH_KEY.exec(key);
  if (!match) return null;
  const year = Number.parseInt(match[1] as string, 10);
  const month = Number.parseInt(match[2] as string, 10);
  return year * 12 + month - 1;
}

function monthKeyForIndex(index: number): string | null {
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  if (year < 0 || year > 9999) return null;
  return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}`;
}